import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { CitySelect } from "@/components/CitySelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";
import {
  Loader2, ChevronLeft, Camera, Image as ImageIcon, User as UserIcon, MapPin,
} from "lucide-react";
import { toast } from "sonner";

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const { language } = useI18n();
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    city_id: user?.city_id || null,
    phone: user?.phone || "",
    whatsapp: user?.whatsapp || "",
  });
  const [avatar, setAvatar] = useState(user?.avatar_url || null);
  const [cover, setCover] = useState(user?.cover_url || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: user.name || "",
        bio: user.bio || "",
        city_id: user.city_id || null,
        phone: user.phone || "",
        whatsapp: user.whatsapp || "",
      }));
      setAvatar(user.avatar_url || null);
      setCover(user.cover_url || null);
    }
  }, [user]);

  const uploadImage = async (file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB image"); return null; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const onAvatarFile = async (e) => {
    const url = await uploadImage(e.target.files?.[0]);
    if (url) setAvatar(url);
  };
  const onCoverFile = async (e) => {
    const url = await uploadImage(e.target.files?.[0]);
    if (url) setCover(url);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        bio: form.bio,
      };
      if (form.city_id) payload.city_id = form.city_id;
      if (avatar) payload.avatar_url = avatar;
      if (cover) payload.cover_url = cover;
      if (form.phone) payload.phone = form.phone;
      if (form.whatsapp) payload.whatsapp = form.whatsapp;
      await api.patch("/users/me", payload);
      await refreshMe?.();
      toast.success(t("editp_saved", language));
      navigate(-1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const topBar = (
    <div className="h-14 px-4 flex items-center gap-2">
      <button
        onClick={() => navigate(-1)}
        data-testid="editp-back-btn"
        className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <h1 className="font-extrabold text-[20px] text-[var(--te-text)] flex-1">{t("editp_title", language)}</h1>
      <button
        onClick={save}
        disabled={busy}
        data-testid="editp-save-btn"
        className="te-btn-primary !py-2 !px-4 text-[13px]"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : t("save", language)}
      </button>
    </div>
  );

  return (
    <MobileShell topBar={topBar}>
      {/* Cover banner */}
      <div
        className="relative h-44 bg-[var(--te-primary)] flex items-center justify-center"
        style={cover ? { backgroundImage: `url(${cover})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
        data-testid="editp-cover"
      >
        <label className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#553098] flex items-center justify-center cursor-pointer hover:bg-[#3D36B0]">
          <Camera size={16} className="text-white" />
          <input type="file" accept="image/*" className="hidden" onChange={onCoverFile} data-testid="editp-cover-upload" />
        </label>
        {!cover && (
          <div className="flex flex-col items-center gap-1 text-white/85">
            <ImageIcon size={24} />
            <span className="text-[13px] font-medium">{t("editp_cover_hint", language)}</span>
          </div>
        )}

        {/* Avatar overlap */}
        <div className="absolute left-1/2 -bottom-12 -translate-x-1/2">
          <div className="relative w-24 h-24 rounded-full bg-[var(--te-input-bg)] border-[3px] border-white shadow-md flex items-center justify-center overflow-hidden">
            {avatar ? (
              <img src={avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <UserIcon size={36} className="text-[var(--te-text-muted)]" />
            )}
            <label className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[var(--te-primary)] border-2 border-white flex items-center justify-center cursor-pointer hover:bg-[var(--te-primary-hover)]">
              <Camera size={13} className="text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={onAvatarFile} data-testid="editp-avatar-upload" />
            </label>
          </div>
        </div>
      </div>

      <div className="pt-16 px-4 pb-6 space-y-5">
        {/* Display name */}
        <Field label={t("editp_display_name", language)}>
          <input
            data-testid="editp-name-input"
            className="te-input text-center font-semibold"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>

        {/* Locality */}
        <Field label={t("editp_locality", language)} eyebrow>
          <CitySelect
            value={form.city_id}
            onChange={(id) => setForm({ ...form, city_id: id })}
            placeholder={language === "ar" ? "اختر القرية…" : language === "he" ? "בחר יישוב…" : "Choose your locality…"}
            testId="editp-city-select"
          />
        </Field>

        {/* Phone */}
        <Field label={t("editp_phone", language)}>
          <input
            data-testid="editp-phone-input"
            className="te-input text-center"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="05X-XXX-XXXX"
            dir="ltr"
          />
        </Field>

        {/* WhatsApp */}
        <Field label={t("editp_whatsapp", language)} eyebrow>
          <input
            data-testid="editp-whatsapp-input"
            className="te-input text-center"
            type="tel"
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            placeholder="+1 555 0100"
            dir="ltr"
          />
        </Field>

        {/* Bio */}
        <Field label={t("editp_bio", language)}>
          <textarea
            data-testid="editp-bio-input"
            className="te-input min-h-[90px] resize-y"
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            dir="auto"
          />
        </Field>
      </div>
    </MobileShell>
  );
}

function Field({ label, children, eyebrow }) {
  return (
    <div>
      <label className={eyebrow ? "te-eyebrow block mb-2" : "te-label"}>{label}</label>
      {children}
    </div>
  );
}
